import { ExportType, RoleId } from '@common/constants';
import { ALL_PRACTICE_AREAS, type PracticeArea } from '@common/enums';
import type { BillingCustomer, BillingTransaction } from '@common/types/billing';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import {
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useMemo, useState } from 'react';
import BillingSummary from '@/components/BillingSummary';
import BillingSummarySkeleton from '@/components/BillingSummary/BillingSummarySkeleton';
import PageWrapper from '@/components/PageWrapper';
import SearchableLawFirmSelect from '@/components/SearchableLawFirmSelect';
import { useLawFirm } from '@/queries/lawFirms';
import {
    getBillingCustomer,
    getBillingTransactions,
    updateBillingPrice,
} from '@/services/BilllingService';
import useIdentityStore from '@/stores/useIdentityStore';
import { theme } from '@/theme';
import { buildInvoiceCustomer } from '@/utils/invoices/customer';
import type { CustomerInfo } from '@/utils/invoices/types';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import TransactionTable from './TransactionTable';

type BillingCadence = 'monthly' | 'semimonthly' | 'daily';

function calculateDateRange(
    cadence: BillingCadence,
    offset: number,
): {
    start: DateTime;
    end: DateTime;
} {
    const base = DateTime.now().setZone('America/Toronto');

    if (cadence === 'daily') {
        const target = base.plus({ days: offset });
        return {
            start: target.startOf('day'),
            end: target.endOf('day'),
        };
    }

    if (cadence === 'semimonthly') {
        // Semimonthly: 24 pay periods per year
        // First half: 1st-15th, Second half: 16th-end of month
        const isCurrentFirstHalf = base.day <= 15;
        // Period index within year: 0 = Jan 1-15, 1 = Jan 16-31, ..., 23 = Dec 16-31
        const currentPeriodInYear = (base.month - 1) * 2 + (isCurrentFirstHalf ? 0 : 1);

        // Apply offset and handle year rollover
        let targetPeriodInYear = currentPeriodInYear + offset;
        let targetYear = base.year;

        while (targetPeriodInYear < 0) {
            targetPeriodInYear += 24;
            targetYear--;
        }
        while (targetPeriodInYear >= 24) {
            targetPeriodInYear -= 24;
            targetYear++;
        }

        // Convert back to month and half
        const targetMonth = Math.floor(targetPeriodInYear / 2) + 1;
        const targetIsFirstHalf = targetPeriodInYear % 2 === 0;

        if (targetIsFirstHalf) {
            return {
                start: DateTime.fromObject(
                    { year: targetYear, month: targetMonth },
                    { zone: 'America/Toronto' },
                ).startOf('month'),
                end: DateTime.fromObject(
                    { year: targetYear, month: targetMonth, day: 15 },
                    { zone: 'America/Toronto' },
                ).endOf('day'),
            };
        }
        return {
            start: DateTime.fromObject(
                { year: targetYear, month: targetMonth, day: 16 },
                { zone: 'America/Toronto' },
            ).startOf('day'),
            end: DateTime.fromObject(
                { year: targetYear, month: targetMonth },
                { zone: 'America/Toronto' },
            ).endOf('month'),
        };
    }

    const target = base.plus({ month: offset });

    return {
        start: target.startOf('month'),
        end: target.endOf('month'),
    };
}

export function matchesPracticeAreaFilter(
    transaction: BillingTransaction,
    practiceAreaFilter: PracticeArea | 'all',
): boolean {
    if (practiceAreaFilter === 'all') return true;
    return transaction.practiceArea === practiceAreaFilter;
}

export default function Billing() {
    const [loggedInUser] = useIdentityStore((state) => [state.user]);
    const isSuperAdmin = !!loggedInUser?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);
    const [selectedLawFirmId, setSelectedLawFirmId] = useState<number | undefined>(undefined);
    const [fromDate, setFromDate] = useState<DateTime | null>(
        DateTime.now().setZone('America/Toronto').startOf('month'),
    );
    const [toDate, setToDate] = useState<DateTime | null>(
        DateTime.now().setZone('America/Toronto').endOf('month'),
    );
    const [billingCadence, setBillingCadence] = useState<BillingCadence>('monthly');
    const [selectedStatus, setSelectedStatus] = useState<ExportType>(ExportType.ALL_COMPLETED);
    const [selectedPracticeArea, setSelectedPracticeArea] = useState<PracticeArea | 'all'>('all');
    const [timeRangeOffset, setTimeRangeOffset] = useState(0);
    const [includeTestFirms, setIncludeTestFirms] = useState(false);
    const [page, setPage] = useState(0);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!isSuperAdmin && loggedInUser?.lawFirm?.lawFirmId) {
            setSelectedLawFirmId(loggedInUser.lawFirm.lawFirmId);
            setIncludeTestFirms(true);
        }
    }, [isSuperAdmin, loggedInUser?.lawFirm?.lawFirmId]);

    const lawFirmIdForQuery = useMemo(() => {
        return isSuperAdmin ? selectedLawFirmId : (loggedInUser?.lawFirm?.lawFirmId ?? undefined);
    }, [isSuperAdmin, loggedInUser?.lawFirm?.lawFirmId, selectedLawFirmId]);

    const { data: selectedLawFirm } = useLawFirm(lawFirmIdForQuery);

    const { data: billingCustomer } = useQuery<BillingCustomer | null>({
        queryKey: ['billing-customer', lawFirmIdForQuery],
        queryFn: async () => {
            if (!lawFirmIdForQuery) return null;
            const response = await getBillingCustomer(lawFirmIdForQuery);
            return response.data;
        },
        enabled: !!lawFirmIdForQuery && selectedLawFirmId !== undefined,
        staleTime: Infinity,
    });

    useEffect(() => {
        const { start, end } = calculateDateRange(billingCadence, timeRangeOffset);
        setFromDate(start);
        setToDate(end);
        setPage(0);
    }, [billingCadence, timeRangeOffset]);

    const { data: transactions = [], isLoading: isTransactionsLoading } = useQuery<
        BillingTransaction[]
    >({
        queryKey: [
            'transactions',
            lawFirmIdForQuery ?? 'all',
            fromDate?.toISO() ?? 'start-null',
            toDate?.toISO() ?? 'end-null',
            includeTestFirms ? 'include-test-firms' : 'exclude-test-firms',
        ],
        queryFn: async () => {
            const response = await getBillingTransactions({
                startDate: fromDate ? (fromDate.startOf('day').toISO() ?? undefined) : undefined,
                endDate: toDate ? (toDate.endOf('day').toISO() ?? undefined) : undefined,
                lawFirmId: lawFirmIdForQuery,
                includeTestFirms,
            });
            return response.data;
        },
        staleTime: Infinity,
    });

    const billableTransactions = useMemo(() => {
        const isCompletedInRange = (completedAt: BillingTransaction['completedAt']): boolean => {
            if (completedAt === null) return false;
            if (!fromDate || !toDate) return true;
            const completed = DateTime.fromISO(
                typeof completedAt === 'string' ? completedAt : new Date(completedAt).toISOString(),
                { setZone: true },
            );
            return completed >= fromDate && completed <= toDate;
        };

        return transactions.filter((tx) => isCompletedInRange(tx.completedAt));
    }, [transactions, fromDate, toDate]);

    const practiceAreaFilteredTransactions = useMemo(
        () =>
            billableTransactions.filter((tx) =>
                matchesPracticeAreaFilter(tx, selectedPracticeArea),
            ),
        [billableTransactions, selectedPracticeArea],
    );

    const changePriceMutation = useMutation({
        mutationFn: updateBillingPrice,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['transactions'] });
            successSnackbar('Price updated');
        },
        onError: () => {
            errorSnackbar('Failed to update price');
        },
    });

    const handleConfirmChangePrice = async (params: {
        prospectId: string;
        templateType: BillingTransaction['templateType'];
        basePrice: number;
    }) => {
        await changePriceMutation.mutateAsync(params);
    };

    const dateRangeLabel =
        fromDate && toDate
            ? billingCadence === 'monthly'
                ? fromDate.toFormat('MMMM yyyy')
                : billingCadence === 'daily'
                  ? fromDate.toFormat('MMM d, yyyy')
                  : `${fromDate.toFormat('MMM d')} - ${toDate.toFormat('MMM d, yyyy')}`
            : '—';

    const fullDateRangeLabel =
        fromDate && toDate
            ? `${fromDate.toFormat('MMM d, yyyy HH:mm')} - ${toDate.toFormat('MMM d, yyyy HH:mm')}`
            : '-';

    const endOfPeriodDateLabel =
        toDate?.toFormat('yyyy-MM-dd') ??
        DateTime.utc().setZone('America/Toronto').toFormat('yyyy-MM-dd');

    const invoiceCustomer: CustomerInfo | null = useMemo(
        () =>
            selectedLawFirmId === undefined
                ? null
                : buildInvoiceCustomer(billingCustomer, selectedLawFirm?.companyName),
        [billingCustomer, selectedLawFirm?.companyName, selectedLawFirmId],
    );

    const practiceAreaFilenameSuffix =
        selectedPracticeArea === 'all' ? '' : `-${selectedPracticeArea}`;

    const invoiceRefNumber =
        selectedLawFirmId === undefined
            ? `${selectedStatus}${practiceAreaFilenameSuffix} - ${endOfPeriodDateLabel}`
            : `${selectedLawFirmId}${practiceAreaFilenameSuffix}-${endOfPeriodDateLabel}`;

    const handleShiftTimeRange = (delta: number) => {
        setTimeRangeOffset((prev) => Math.min(prev + delta, 0));
    };

    const isAtCurrentPeriod = timeRangeOffset >= 0;

    const handleChangeCadence = (_: unknown, value: BillingCadence | null) => {
        if (!value) return;
        setBillingCadence(value);
        setTimeRangeOffset(0); // Reset to current period
    };

    return (
        <PageWrapper title="Billing">
            <Stack spacing={3}>
                <Card
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.neutral.N_200}`,
                        boxShadow: 'none',
                    }}
                >
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack>
                                <Typography variant="h6" fontWeight={700}>
                                    {dateRangeLabel}
                                </Typography>
                                <Typography variant="body2" color={theme.palette.neutral.N_700}>
                                    {fullDateRangeLabel}
                                </Typography>
                                <Stack direction="row" spacing={1} marginTop={1}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleShiftTimeRange(-1)}
                                        startIcon={<ArrowBackIosNew fontSize="small" />}
                                    >
                                        Prev
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleShiftTimeRange(-timeRangeOffset)}
                                    >
                                        Current
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleShiftTimeRange(1)}
                                        endIcon={<ArrowForwardIos fontSize="small" />}
                                        disabled={isAtCurrentPeriod}
                                    >
                                        Next
                                    </Button>
                                </Stack>
                                <Stack marginTop={1}>
                                    <ToggleButtonGroup
                                        value={billingCadence}
                                        exclusive
                                        size="small"
                                        fullWidth
                                        onChange={handleChangeCadence}
                                    >
                                        <ToggleButton value="daily">Daily</ToggleButton>
                                        <ToggleButton value="semimonthly">Semimonthly</ToggleButton>
                                        <ToggleButton value="monthly">Monthly</ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                            </Stack>
                            {isSuperAdmin && (
                                <Stack sx={{ minWidth: 220 }}>
                                    <SearchableLawFirmSelect
                                        value={selectedLawFirmId}
                                        includeTestFirms={includeTestFirms}
                                        onChange={(value) => {
                                            setSelectedLawFirmId(value);
                                            setPage(0);
                                        }}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={includeTestFirms}
                                                onChange={(event) => {
                                                    setIncludeTestFirms(event.target.checked);
                                                    setPage(0);
                                                }}
                                            />
                                        }
                                        label="Include test firms"
                                    />
                                </Stack>
                            )}
                        </Stack>
                    </CardContent>
                </Card>

                <Suspense fallback={<BillingSummarySkeleton />}>
                    <BillingSummary
                        transactions={practiceAreaFilteredTransactions}
                        dateRangeLabel={dateRangeLabel}
                        practiceAreaFilter={selectedPracticeArea}
                        invoicePayload={{
                            customer: invoiceCustomer,
                            invoice: {
                                number: invoiceRefNumber,
                                date: endOfPeriodDateLabel,
                            },
                        }}
                    />
                </Suspense>

                <TransactionTable
                    isLoading={isTransactionsLoading}
                    transactions={transactions}
                    selectedStatus={selectedStatus}
                    onSelectStatus={setSelectedStatus}
                    selectedPracticeArea={selectedPracticeArea}
                    onSelectPracticeArea={(value) => {
                        setSelectedPracticeArea(value);
                        setPage(0);
                    }}
                    practiceAreaOptions={ALL_PRACTICE_AREAS}
                    dateRangeLabel={dateRangeLabel}
                    fromDate={fromDate}
                    toDate={toDate}
                    isSelectable={isSuperAdmin}
                    isSuperAdmin={isSuperAdmin ?? false}
                    onConfirmChangePrice={handleConfirmChangePrice}
                    isChangePriceLoading={changePriceMutation.isPending}
                    page={page}
                    setPage={setPage}
                />
            </Stack>
        </PageWrapper>
    );
}
